import { lineErrorText } from '../offers/lineErrors';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orderEditable, type Contact, type Order } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { ContactPicker } from '../../components/ContactPicker';
import { bpText, parseBp } from '../../money';
import { draftFromLine, emptyLine, LinesEditor, linesBody, linesProblem, useUnits, type LineDraft } from '../offers/LinesEditor';
import { RecipientCard } from '../offers/RecipientCard';
import './orders.css';

/**
 * Direktauftrag anlegen oder einen Auftrag bearbeiten (KON-125). Positionen wie im Angebot, aus den Produkten oder frei. Die Nummer vergibt Wirkia beim Anlegen,
 * im Formular steht sie nur zur Ansicht. Ein abgerechneter oder stornierter Auftrag lässt sich nicht bearbeiten. Mit ?kontakt=… ist der Kunde vorbelegt.
 */
export function OrderEditorPage() {
  const { id } = useParams();
  const isCreate = !id;
  const [params] = useSearchParams();
  const contactParam = isCreate ? params.get('kontakt') : null;
  const navigate = useNavigate();
  const client = useQueryClient();
  const { units, symbols } = useUnits();

  const existing = useQuery({ queryKey: ['order', id], queryFn: async () => (await api<{ data: Order }>('GET', `/orders/${id}`)).data, enabled: !isCreate });
  const defaults = useQuery({ queryKey: ['order-defaults'], queryFn: async () => (await api<{ data: { smallBusiness: boolean } }>('GET', '/orders/defaults')).data, enabled: isCreate });
  const preset = useQuery({ queryKey: ['contact', contactParam], queryFn: async () => (await api<{ data: Contact }>('GET', `/contacts/${contactParam}`)).data, enabled: !!contactParam });

  const [contact, setContact] = useState<{ id: string; name: string } | null>(null);
  const [subject, setSubject] = useState('');
  const [customerReference, setCustomerReference] = useState('');
  const [serviceStart, setServiceStart] = useState('');
  const [serviceEnd, setServiceEnd] = useState('');
  const [notes, setNotes] = useState('');
  const [discount, setDiscount] = useState('');
  const [lines, setLines] = useState<LineDraft[]>(() => (isCreate ? [emptyLine()] : []));
  const [formError, setFormError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current || !existing.data) return;
    const o = existing.data;
    loaded.current = true;
    setContact({ id: o.contactId, name: o.contactName });
    setSubject(o.subject);
    setCustomerReference(o.customerReference ?? '');
    setServiceStart(o.serviceStart ?? '');
    setServiceEnd(o.serviceEnd ?? '');
    setNotes(o.notes ?? '');
    setDiscount(bpText(o.discountBp));
    setLines(o.lines.map(draftFromLine));
  }, [existing.data]);
  useEffect(() => {
    if (preset.data && !contact) setContact({ id: preset.data.id, name: preset.data.name });
  }, [preset.data, contact]);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        contactId: contact?.id,
        subject: subject.trim(),
        customerReference: customerReference.trim(),
        notes: notes.trim(),
        discountBp: parseBp(discount || '0') ?? 0,
        lines: linesBody(lines),
      };
      return isCreate
        ? api<{ data: Order }>('POST', '/orders', { ...body, serviceStart: serviceStart || undefined, serviceEnd: serviceEnd || undefined })
        : api<{ data: Order }>('PATCH', `/orders/${id}`, { ...body, serviceStart: serviceStart || null, serviceEnd: serviceEnd || null });
    },
    onSuccess: (res) => {
      client.invalidateQueries({ queryKey: ['orders'] });
      client.setQueryData(['order', res.data.id], res.data);
      navigate(`/auftraege/${res.data.id}`, { replace: true });
    },
    onError: (e) => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (e instanceof ApiError && e.status === 422) {
        setErrors(e.fields);
        setFormError(lineErrorText(e.fields) ?? 'Bitte prüfen Sie Ihre Eingaben.');
      } else setFormError(e instanceof ApiError ? e.message : 'Der Auftrag konnte nicht gespeichert werden.');
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setErrors({});
    setFormError(null);
    if (!contact) {
      setErrors({ contactId: 'Bitte einen Kunden wählen.' });
      setFormError('Bitte prüfen Sie Ihre Eingaben.');
      return;
    }
    if (lines.length === 0) {
      setFormError('Ein Auftrag braucht mindestens eine Position.');
      return;
    }
    const problem = linesProblem(lines, units ? symbols : null);
    if (problem) {
      setFormError(problem);
      return;
    }
    save.mutate();
  }

  if (!isCreate && existing.isLoading) return <p className="muted">Wird geladen …</p>;
  if (!isCreate && (existing.isError || !existing.data)) {
    return (
      <div className="card narrow">
        <h1>Auftrag nicht gefunden</h1>
        <Link to="/auftraege">Zur Liste</Link>
      </div>
    );
  }
  if (existing.data && !orderEditable(existing.data.status)) {
    return (
      <div className="card narrow">
        <h1>Nicht änderbar</h1>
        <p>{existing.data.status === 'invoiced' ? 'Dieser Auftrag ist abgerechnet und lässt sich nicht mehr ändern.' : 'Dieser Auftrag ist storniert. Öffnen Sie ihn zuerst wieder.'}</p>
        <Link to={`/auftraege/${id}`}>Zum Auftrag</Link>
      </div>
    );
  }
  const error = (key: string) => (errors[key] ? <small className="field__error" role="alert">{errors[key]}</small> : null);
  const smallBusiness = (isCreate ? defaults.data?.smallBusiness : existing.data?.smallBusiness) ?? false;
  const back = isCreate ? (contactParam ? `/kontakte/${contactParam}` : '/auftraege') : `/auftraege/${id}`;

  return (
    <>
      <p><Link to={back} className="back-link">← {isCreate ? (contactParam ? 'Zum Kontakt' : 'Alle Aufträge') : 'Zum Auftrag'}</Link></p>
      <div className="page-head">
        <h1>{isCreate ? 'Neuer Auftrag' : `${existing.data!.number} bearbeiten`}</h1>
      </div>
      {isCreate && <p className="muted">Für einen Auftrag ohne Angebot, etwa einen Kaufvertrag vor Ort. Aus einem angenommenen Angebot entsteht der Auftrag von selbst. Die Nummer vergibt Wirkia beim Speichern.</p>}
      {existing.data?.offerNumber && <p className="muted">Entstanden aus dem Angebot {existing.data.offerNumber}. Änderungen hier ändern das Angebot nicht.</p>}

      <form className="card offer-editor" onSubmit={submit} noValidate>
        {formError && <div className="alert alert--error" role="alert">{formError}</div>}
        <div className="form-grid">
          <div className="field">
            <span className="field__label">Kunde <abbr title="Pflichtfeld">*</abbr></span>
            <ContactPicker value={contact} onChange={setContact} invalid={!!errors.contactId} label="Kunde" hint={<>Neue Kunden legen Sie unter <Link to="/kontakte/neu">Kontakte</Link> an.</>} />
            {contact && <RecipientCard contactId={contact.id} />}
            {error('contactId')}
          </div>
          <label className="field">
            <span>Betreff <abbr title="Pflichtfeld">*</abbr></span>
            <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} aria-invalid={!!errors.subject} />
            {error('subject')}
          </label>
          <label className="field">
            <span>Bestellnummer des Kunden</span>
            <input className="input" value={customerReference} onChange={(e) => setCustomerReference(e.target.value)} maxLength={100} />
            <small className="muted">Freiwillig. Steht später auf der Rechnung, wenn der Kunde sie verlangt.</small>
          </label>
        </div>
        <div className="form-grid">
          <label className="field">
            <span>Leistung von</span>
            <input className="input" type="date" value={serviceStart} onChange={(e) => { setServiceStart(e.target.value); if (!serviceEnd || serviceEnd < e.target.value) setServiceEnd(e.target.value); }} aria-invalid={!!errors.serviceStart} />
            {error('serviceStart')}
          </label>
          <label className="field">
            <span>Leistung bis</span>
            <input className="input" type="date" value={serviceEnd} min={serviceStart || undefined} onChange={(e) => setServiceEnd(e.target.value)} aria-invalid={!!errors.serviceEnd} />
            {error('serviceEnd')}
          </label>
          <p className="muted small order-range-hint">Der Leistungszeitraum steht später als Leistungsdatum auf der Rechnung.</p>
        </div>

        <h2 className="offer-editor__h">Positionen</h2>
        <LinesEditor lines={lines} setLines={setLines} discount={discount} setDiscount={setDiscount} smallBusiness={smallBusiness} fixUnits={isCreate} />

        <label className="field">
          <span>Interne Notiz</span>
          <textarea className="input textarea textarea--short" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={10000} />
          <small className="muted">Nur für Ihr Team, zum Beispiel Zugang zur Wohnung oder Absprachen mit dem Kunden.</small>
        </label>
        <div className="form-actions">
          <button className="btn btn--primary" type="submit" disabled={save.isPending}>{save.isPending ? 'Wird gespeichert …' : isCreate ? 'Auftrag anlegen' : 'Speichern'}</button>
          <Link className="btn btn--secondary" to={back}>Abbrechen</Link>
        </div>
      </form>
    </>
  );
}
